import React, { createContext, useContext, useMemo, useRef, useState } from "react";

interface PerformanceValuesContextType {
  // Strum percentage (0-1)
  strum: number;

  // Groove (-1 to 1)
  groove: number;

  // Voicing (1-4)
  voicing: number;
}

interface PerformanceActionsContextType {
  setStrum: (value: number) => void;
  setGroove: (value: number) => void;
  setVoicing: (value: number) => void;
  getStrum: () => number;
  getGroove: () => number;
  getVoicing: () => number;
}

const PerformanceValuesContext = createContext<PerformanceValuesContextType>({
  strum: 0,
  groove: 0,
  voicing: 1,
});

const PerformanceActionsContext = createContext<PerformanceActionsContextType>({
  setStrum: () => {},
  setGroove: () => {},
  setVoicing: () => {},
  getStrum: () => 0,
  getGroove: () => 0,
  getVoicing: () => 1,
});

export const usePerformanceValues = () => useContext(PerformanceValuesContext);
export const usePerformanceActions = () => useContext(PerformanceActionsContext);

// Backward-compatible combined hook for value + actions consumers.
export const usePerformance = () => {
  const values = usePerformanceValues();
  const actions = usePerformanceActions();
  return {
    ...values,
    ...actions,
  };
};

// Provider component
export const PerformanceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // State for performance parameters
  const [strum, setStrum] = useState<number>(0);
  const [groove, setGroove] = useState<number>(0);
  const [voicing, setVoicing] = useState<number>(1);
  const strumRef = useRef<number>(strum);
  const grooveRef = useRef<number>(groove);
  const voicingRef = useRef<number>(voicing);

  React.useEffect(() => {
    strumRef.current = strum;
  }, [strum]);

  React.useEffect(() => {
    grooveRef.current = groove;
  }, [groove]);

  React.useEffect(() => {
    voicingRef.current = voicing;
  }, [voicing]);

  const values = useMemo<PerformanceValuesContextType>(
    () => ({
      strum,
      groove,
      voicing,
    }),
    [strum, groove, voicing]
  );

  const actions = useMemo<PerformanceActionsContextType>(
    () => ({
      setStrum,
      setGroove,
      setVoicing,
      getStrum: () => strumRef.current,
      getGroove: () => grooveRef.current,
      getVoicing: () => voicingRef.current,
    }),
    []
  );

  return (
    <PerformanceActionsContext.Provider value={actions}>
      <PerformanceValuesContext.Provider value={values}>
        {children}
      </PerformanceValuesContext.Provider>
    </PerformanceActionsContext.Provider>
  );
};
